import { toPng } from 'html-to-image';
import { PDFDocument } from 'pdf-lib';

export type ReportRow = {
  label: string;
  value: unknown;
};

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

export const exportCompressoresExcel = ({
  fileName = 'compressores-relatorio.xls',
  inputRows,
  resultRows,
}: {
  fileName?: string;
  inputRows: ReportRow[];
  resultRows: ReportRow[];
}) => {
  const workbookHtml = `
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="UTF-8" />
<!--[if gte mso 9]><xml>
<x:ExcelWorkbook><x:ExcelWorksheets>
<x:ExcelWorksheet><x:Name>Dados de Entrada</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet>
<x:ExcelWorksheet><x:Name>Resultados</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet>
</x:ExcelWorksheets></x:ExcelWorkbook>
</xml><![endif]-->
<style>
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #cccccc; padding: 6px; }
  th { background: #e9f2ff; text-align: left; }
</style>
</head>
<body>
<table>
  <tr><th colspan="2">Dados de Entrada</th></tr>
  ${inputRows.map((row) => `<tr><td>${escapeHtml(row.label)}</td><td>${escapeHtml(row.value)}</td></tr>`).join('')}
</table>
<br />
<table>
  <tr><th colspan="2">Resultados</th></tr>
  ${resultRows.map((row) => `<tr><td>${escapeHtml(row.label)}</td><td>${escapeHtml(row.value)}</td></tr>`).join('')}
</table>
</body>
</html>`;

  const blob = new Blob([workbookHtml], {
    type: 'application/vnd.ms-excel;charset=utf-8;',
  });

  downloadBlob(blob, fileName);
};

const createSliceCanvas = (
  canvas: HTMLCanvasElement,
  imageWidth: number,
  offsetY: number,
  currentSliceHeight: number
) => {
  const sliceCanvas = document.createElement('canvas');
  const sliceCtx = sliceCanvas.getContext('2d');

  if (!sliceCtx) {
    throw new Error('Não foi possível obter o contexto do canvas do recorte.');
  }

  sliceCanvas.width = imageWidth;
  sliceCanvas.height = currentSliceHeight;

  sliceCtx.drawImage(
    canvas,
    0,
    offsetY,
    imageWidth,
    currentSliceHeight,
    0,
    0,
    imageWidth,
    currentSliceHeight
  );

  return sliceCanvas;
};

export const exportElementToPdf = async ({
  element,
  fileName = 'compressores-relatorio.pdf',
}: {
  element: HTMLElement;
  title?: string;
  fileName?: string;
}) => {
  const dataUrl = await toPng(element, {
    cacheBust: true,
    pixelRatio: 2,
    backgroundColor: '#ffffff',
  });

  const image = await loadImage(dataUrl);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Não foi possível obter o contexto do canvas.');
  }

  canvas.width = image.width;
  canvas.height = image.height;
  ctx.drawImage(image, 0, 0);

  const pdfDoc = await PDFDocument.create();

  const pageWidth = 595.28;
  const pageHeight = 841.89;
  const margin = 20;
  const usableWidth = pageWidth - margin * 2;
  const usableHeight = pageHeight - margin * 2;
  const scale = usableWidth / image.width;
  const sliceHeightPx = Math.floor(usableHeight / scale);

  const tasks: Array<{
    offsetY: number;
    currentSliceHeight: number;
    sliceDataUrl: string;
    renderedHeight: number;
  }> = [];

  let offsetY = 0;

  while (offsetY < image.height) {
    const currentSliceHeight = Math.min(sliceHeightPx, image.height - offsetY);
    const sliceCanvas = createSliceCanvas(canvas, image.width, offsetY, currentSliceHeight);

    tasks.push({
      offsetY,
      currentSliceHeight,
      sliceDataUrl: sliceCanvas.toDataURL('image/png'),
      renderedHeight: currentSliceHeight * scale,
    });

    offsetY += currentSliceHeight;
  }

  const embeddedImages = await Promise.all(
    tasks.map((task) => pdfDoc.embedPng(task.sliceDataUrl))
  );

  tasks.forEach((task, index) => {
    const page = pdfDoc.addPage([pageWidth, pageHeight]);

    page.drawImage(embeddedImages[index], {
      x: margin,
      y: pageHeight - margin - task.renderedHeight,
      width: usableWidth,
      height: task.renderedHeight,
    });
  });

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  downloadBlob(blob, fileName);
};

export const exportCompressoresPdf = exportElementToPdf;